import * as _ from 'lodash';
import { useState } from 'react';
import Select from 'react-select-2';
import type { VoidFunctionComponent } from 'react';
import type { ValueType } from 'react-select-2/lib/types';

import { vulnSources } from '../../../../shared/security/consts';
import { BannerStyle } from '../../../../typings/enums';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useAsyncStatus } from '../../../hooks';
import { post } from '../../../redux/api';
import { selectOptionFactory } from '../../../utils';
import { InlineLoaderButton } from '../../generic';
import Banner from '../../generic/Banner';
import Flex from '../../generic/Flex';
import type { FailedFetchResponse } from '../../../helpers/errors';

function isConflictResponse(res: any) {
  return res?.status === 409;
}

const concurrencyOptions = [2, 4, 8, 16, 32];

const SourceVulnTransformComponent: VoidFunctionComponent = () => {
  const { onConfirm } = useDialogContext();
  const [source, setSource] = useState<string>(_.head(vulnSources) ?? '');
  const [concurrency, setConcurrency] = useState<number>(16);
  const [isShowingConflictAlert, setIsShowingConflictAlert] = useState(false);
  const { status, onPending, onSuccess, onError } = useAsyncStatus();

  const submitTransform = async () => {
    onPending();
    try {
      await post(`/api/vulns/transform`, { source, concurrency });
      setIsShowingConflictAlert(false);
      onSuccess();
    } catch (err) {
      if (isConflictResponse(err)) {
        setIsShowingConflictAlert(true);
        onSuccess();
        return;
      }

      onError(err as FailedFetchResponse);
    }
  };

  return (
    <div className="card">
      <h3 className="card-title padding-bottom-sm">
        Transform Vulns from Source
      </h3>
      {isShowingConflictAlert && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body="A transform job already exists for this source. Try again later."
            bannerStyle={BannerStyle.Medium}
          />
        </div>
      )}
      {status.error && (
        <div className="padding-bottom-md">
          <Banner
            isDismissible
            onDismiss={() => setIsShowingConflictAlert(false)}
            title="Failed"
            body={status.error}
            bannerStyle={BannerStyle.High}
          />
        </div>
      )}

      <Flex justify="left" align="flex-end" gap={10}>
        <div className="width-half">
          <div>
            Source:
          </div>
          <Select
            value={selectOptionFactory(source)}
            options={vulnSources.map((f) => selectOptionFactory(f))}
            onChange={(option: ValueType<{ value: string; label: string }>) => {
              setSource(option?.value);
            }}
          />
        </div>
        <div className="width-quarter">
          <div>
            Concurrency:
          </div>
          <Select<{ label: string; value: number }>
            value={selectOptionFactory<number>(concurrency)}
            options={concurrencyOptions.map((f) => selectOptionFactory(f))}
            onChange={(option: ValueType<{ label: string; value: number }>) => {
              setConcurrency(option?.value ? Number(option.value) : 16);
            }}
          />
        </div>
        <InlineLoaderButton
          onClick={() => {
            onConfirm({
              title: 'Are you sure?',
              body: 'Vulnerability transform can be a long running process, depending on how many vulns exist for the source. Are you sure you want to proceed?',
              onConfirm: submitTransform,
            });
          }}
          isLoading={status.loading}
          isDisabled={status.loading}
        >
          Submit
        </InlineLoaderButton>
      </Flex>
    </div>
  );
};

export default SourceVulnTransformComponent;
